import { Box } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { editBlockedReasonId } from '~/components/Structures/editBlockedMessage';
import { InterpolateIcon } from '~/assets/workspace-icons';
import { useRecoilState } from 'recoil';
import FloatingPanel from '~/components/toolbar/shared/FloatingPanel';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import { buildToolTooltip } from '~/components/toolbar/shared/toolTooltip';
import { useStructureToolGating } from '~/hooks/target/useStructureToolGating';
import { openFloatingPanelState } from '~/state/atoms/toolsAtoms';
import InterpolationOperation from './slice/InterpolationOperation';

// Only interpolate lives here: clearing contours belongs to the eraser
// and to the deletions offered on the structure itself. Interpolate
// creates contours rather than cleaning them, and keeps its own preview
// machinery.
export default function SliceOperationsSection() {
    // Interpolate modifies the selected ROI's contours in place.
    const {
        selectedRoiContourEditDisabled: isDisabled,
        selectionEditBlocker,
        isPrimaryImageIncomplete,
        imageIncompleteReason,
    } = useStructureToolGating();
    const intl = useIntl();

    const blockedReason = isPrimaryImageIncomplete
        ? imageIncompleteReason
        : selectionEditBlocker
          ? intl.formatMessage({
                id: editBlockedReasonId(selectionEditBlocker),
            })
          : undefined;

    const interpolateAnchorRef = useRef<HTMLElement | null>(null);

    // Disabling the ToolbarButton doesn't close an already-open panel.
    const [openPanel, setOpenPanel] = useRecoilState(openFloatingPanelState);
    useEffect(() => {
        if (isDisabled && openPanel === 'interpolation') {
            setOpenPanel(null);
        }
    }, [isDisabled, openPanel, setOpenPanel]);
    const closePanel = useCallback(() => setOpenPanel(null), [setOpenPanel]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25,
            }}
        >
            <ToolbarButton
                icon={<InterpolateIcon />}
                tooltip={buildToolTooltip(
                    intl,
                    intl.formatMessage({ id: 'toolbar.interpolation.tooltip' }),
                    blockedReason
                )}
                tooltipPlacement="left"
                disabled={isDisabled}
                panelId="interpolation"
                anchorRef={interpolateAnchorRef}
            />
            <FloatingPanel
                panelId="interpolation"
                anchorEl={interpolateAnchorRef.current}
                placement="left-start"
            >
                <InterpolationOperation onClose={closePanel} />
            </FloatingPanel>
        </Box>
    );
}
